import { PaperClipIcon } from "@heroicons/react/solid"
import { useEffect, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { getStudent, ninjaMarkPaid, generateCert, updateStudent } from "../common/adminApi"
import { getCertificate, getCourts } from "../common/api"
import PageWrapper from '../common/PageWrapper'
import StudentProgressBadge from "./StudentProgressBadge"
import { useRecoilValue } from "recoil";
import { authAtom } from '../state';
import InputMask from "react-input-mask"
import listStates from "../common/listStates"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const isValidDob = (dob) => {
  if (dob.length !== 10) return false
  const parts = dob.split('/')
  if (parts.length !== 3 || parts[2].length !== 4) return false
  const [month, day, year] = parts.map(Number)
  const date = new Date(year, month - 1, day)
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  )
}

const Student = () => {
  const location = useLocation()
  const params = useParams()

  const [ studentId ] = useState(location.state?.student?.id ?? params.studentId)
  const [ student, setStudent ] = useState(null)
  const [ certUrl, setCertUrl ] = useState(null)
  const [ markPaidLoading, setMarkPaidLoading ] = useState(false)
  const [ completionDate, setCompletionDate ] = useState("")
  const [ certLoading, setCertLoading ] = useState(false)

  const [ editMode, setEditMode ] = useState(false)
  const [ editData, setEditData ] = useState({})
  const [ editErrors, setEditErrors ] = useState(new Set())
  const [ editLoading, setEditLoading ] = useState(false)
  const [ editSuccess, setEditSuccess ] = useState(false)
  const [ courts, setCourts ] = useState([])

  const auth = useRecoilValue(authAtom)

  useEffect(() => {
    Promise.all([
      getStudent(studentId),
      getCertificate(studentId),
      getCourts()
    ])
    .then((results) => {
      setStudent(results[0])
      setCertUrl(results[1].url)
      setCourts(results[2])
    })
    .catch((err) => {
      console.log(err)
      alert('Error getting student details')
    })
  }, [ studentId ])

  const changeCompletionDate = (e) => {
    setCompletionDate(e.target.value)
  }

  const handleGenerateCert = () => {
    setCertLoading(true)
    generateCert(studentId, completionDate ?? null)
    .then(() => {
      window.location.reload(false);
    })
  }

  const handleMarkPaid = () => {
    setMarkPaidLoading(true)
    ninjaMarkPaid(auth.id, studentId)
    .then(() => {
      getStudent(studentId)
      .then((newStudent) => setStudent(newStudent))
      .catch((err) => {
        console.log(err)
        location.reload()
      })
    })
    .catch((err) => {
      alert('Error marking student as paid')
    })
  }

  const handleEditToggle = () => {
    if (!editMode && student) {
      setEditData({
        first_name: student.first_name ?? '',
        last_name: student.last_name ?? '',
        email: student.email ?? '',
        dl_number: student.dl_number ?? '',
        address: student.address ?? '',
        city: student.city ?? '',
        state: student.state ?? '',
        zip: student.zip ?? '',
        dob: student.dob_formatted?.replace(/-/g, '/') ?? '',
        court_id: student.court_id ?? ''
      })
      setEditErrors(new Set())
      setEditSuccess(false)
    }
    setEditMode(prev => !prev)
  }

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value })
  }

  const fieldClass = (field) =>
    `appearance-none block w-full px-3 py-2 border ${
      editErrors.has(field) ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`

  const selectClass = (field) =>
    `mt-1 block w-full pl-3 pr-10 py-2 text-base ${
      editErrors.has(field) ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
    } focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md`

  const handleEditSubmit = (e) => {
    e.preventDefault()

    const trimmedEmail = editData.email.trim()

    let currentErrors = []
    if (!editData.first_name.trim()) currentErrors.push('first_name')
    if (!editData.last_name.trim()) currentErrors.push('last_name')
    if (!trimmedEmail || !EMAIL_REGEX.test(trimmedEmail)) currentErrors.push('email')
    if (!editData.dl_number.trim()) currentErrors.push('dl_number')
    if (!isValidDob(editData.dob)) currentErrors.push('dob')
    if (!editData.address.trim()) currentErrors.push('address')
    if (!editData.city.trim()) currentErrors.push('city')
    if (!editData.state) currentErrors.push('state')
    if (!editData.zip.trim()) currentErrors.push('zip')
    if (!editData.court_id) currentErrors.push('court_id')

    if (currentErrors.length > 0) {
      setEditErrors(new Set(currentErrors))
      return
    }

    setEditErrors(new Set())
    setEditLoading(true)
    setEditSuccess(false)

    updateStudent(studentId, { ...editData, email: trimmedEmail })
    .then(() => {
      return getStudent(studentId)
    })
    .then((updated) => {
      setStudent(updated)
      setEditLoading(false)
      setEditSuccess(true)
      setEditMode(false)
    })
    .catch((err) => {
      setEditLoading(false)
      alert(err.response?.data?.error ?? 'Error saving changes')
    })
  }

  return (
    <PageWrapper title={`${student?.first_name} ${student?.last_name}`}>

      <div className="flex justify-between mt-5 pb-3">
        <div>
          <span className="text-gray-500-bold">Course status: <StudentProgressBadge student={student} /></span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-500">
            Registered {student?.created_at_formatted}
          </span>
          {auth?.type === 'super_admin' && (
            <button
              onClick={handleEditToggle}
              type="button"
              className="py-1 px-3 border border-indigo-600 rounded-md text-xs font-medium text-indigo-600 hover:bg-indigo-50"
            >
              {editMode ? 'Cancel' : 'Edit'}
            </button>
          )}
        </div>
      </div>

      <div className="overflow-hidden bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">{student?.first_name} {student?.last_name}</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">DL#: {student?.dl_number}</p>
          {
            (!student?.payment_at && auth.type == 'super_admin') &&
            <>
              <button
                className="flex justify-center py-1 px-2 mt-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={handleMarkPaid}
              >
                { markPaidLoading ? "Loading..." : "Mark as paid" }
              </button>
            </>
          }
          {
            student?.payment_at &&
              <span className="text-sm text-gray-500">
                Paid on {student?.payment_at_formatted}
              </span>
          }
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Email address</dt>
              <dd className="mt-1 text-sm text-gray-900">{student?.email}</dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Court</dt>
              <dd className="mt-1 text-sm text-gray-900">{student?.court_name}</dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">DOB</dt>
              <dd className="mt-1 text-sm text-gray-900">{student?.dob_formatted}</dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">Address</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <span>{student?.address}</span>
                <br/>
                <span>{student?.city}, {student?.state} {student?.zip}</span>
              </dd>
            </div>

            {
              certUrl &&
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Attachments</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <ul role="list" className="divide-y divide-gray-200 rounded-md border border-gray-200">
                      <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                        <div className="flex w-0 flex-1 items-center">
                          <PaperClipIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                          <span className="ml-2 w-0 flex-1 truncate">Certificate_Of_Completion.pdf</span>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          <a href={certUrl} className="font-medium text-indigo-600 hover:text-indigo-500">
                            Download
                          </a>
                        </div>
                      </li>
                    </ul>
                  </dd>
                </div>
            }

            {auth?.type === "super_admin" && <div>
              <input
                id="completionDate"
                name="completionDate"
                type="text"
                placeholder="January 1, 2023"
                onChange={changeCompletionDate}
                value={completionDate}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <button
                disabled={certLoading}
                className="flex justify-center py-1 px-2 mt-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={handleGenerateCert}
              >
                { certLoading ? "Loading..." : "Generate new certificate" }
              </button>
            </div>}

          </dl>
        </div>
      </div>

      {editSuccess && !editMode && (
        <div className="mt-4 rounded-md bg-green-50 p-4">
          <p className="text-green-700 text-sm">Student information updated successfully.</p>
        </div>
      )}

      {auth?.type === 'super_admin' && editMode && (
        <div className="mt-6 overflow-hidden bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Edit Student Information</h3>
          </div>
          <div className="px-4 py-5 sm:px-6">
            <form className="space-y-6">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                <div className="sm:col-span-3">
                  <label htmlFor="edit_first_name" className="block text-sm font-medium text-gray-700">First name</label>
                  <div className="mt-1">
                    <input
                      id="edit_first_name"
                      name="first_name"
                      type="text"
                      value={editData.first_name}
                      onChange={handleEditChange}
                      className={fieldClass('first_name')}
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="edit_last_name" className="block text-sm font-medium text-gray-700">Last name</label>
                  <div className="mt-1">
                    <input
                      id="edit_last_name"
                      name="last_name"
                      type="text"
                      value={editData.last_name}
                      onChange={handleEditChange}
                      className={fieldClass('last_name')}
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="edit_email" className="block text-sm font-medium text-gray-700">Email address</label>
                  <div className="mt-1">
                    <input
                      id="edit_email"
                      name="email"
                      type="text"
                      value={editData.email}
                      onChange={handleEditChange}
                      className={fieldClass('email')}
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="edit_dl_number" className="block text-sm font-medium text-gray-700">Driver's License Number</label>
                  <div className="mt-1">
                    <input
                      id="edit_dl_number"
                      name="dl_number"
                      type="text"
                      value={editData.dl_number}
                      onChange={handleEditChange}
                      className={fieldClass('dl_number')}
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="edit_dob" className="block text-sm font-medium text-gray-700">Date of Birth</label>
                  <div className="mt-1">
                    <InputMask
                      mask='99/99/9999'
                      maskPlaceholder={null}
                      id="edit_dob"
                      name="dob"
                      type="text"
                      placeholder="07/13/1998"
                      value={editData.dob}
                      onChange={handleEditChange}
                      className={fieldClass('dob')}
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="edit_address" className="block text-sm font-medium text-gray-700">Address</label>
                  <div className="mt-1">
                    <input
                      id="edit_address"
                      name="address"
                      type="text"
                      value={editData.address}
                      onChange={handleEditChange}
                      className={fieldClass('address')}
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="edit_city" className="block text-sm font-medium text-gray-700">City</label>
                  <div className="mt-1">
                    <input
                      id="edit_city"
                      name="city"
                      type="text"
                      value={editData.city}
                      onChange={handleEditChange}
                      className={fieldClass('city')}
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="edit_state" className="block text-sm font-medium text-gray-700">State</label>
                  <div className="mt-1">
                    <select
                      id="edit_state"
                      name="state"
                      value={editData.state}
                      onChange={handleEditChange}
                      className={selectClass('state')}
                    >
                      {listStates().map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="edit_zip" className="block text-sm font-medium text-gray-700">Zip</label>
                  <div className="mt-1">
                    <input
                      id="edit_zip"
                      name="zip"
                      type="text"
                      value={editData.zip}
                      onChange={handleEditChange}
                      className={fieldClass('zip')}
                    />
                  </div>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="edit_court_id" className="block text-sm font-medium text-gray-700">Court</label>
                  <div className="mt-1">
                    <select
                      id="edit_court_id"
                      name="court_id"
                      value={editData.court_id}
                      onChange={handleEditChange}
                      className={selectClass('court_id')}
                    >
                      {courts.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

              </div>

              {editErrors.size > 0 && (
                <div className="rounded-md bg-red-50 p-4">
                  <ul className="text-red-700 text-sm space-y-1 list-disc list-inside">
                    {editErrors.has('first_name') && <li>First name is required</li>}
                    {editErrors.has('last_name') && <li>Last name is required</li>}
                    {editErrors.has('email') && <li>Please enter a valid email address (no spaces allowed)</li>}
                    {editErrors.has('dl_number') && <li>Driver's license number is required</li>}
                    {editErrors.has('dob') && <li>Please enter a complete date of birth in MM/DD/YYYY format</li>}
                    {editErrors.has('address') && <li>Address is required</li>}
                    {editErrors.has('city') && <li>City is required</li>}
                    {editErrors.has('state') && <li>State is required</li>}
                    {editErrors.has('zip') && <li>Zip code is required</li>}
                    {editErrors.has('court_id') && <li>Please select a court</li>}
                  </ul>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleEditSubmit}
                  disabled={editLoading}
                  type="button"
                  className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {editLoading ? 'Saving...' : 'Save changes'}
                </button>
                <button
                  onClick={handleEditToggle}
                  type="button"
                  className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </PageWrapper>
  )
}

export default Student
